/**
 * The Upload bar with its own sign-in sheet, so any page can drop it in: the Simple page's hero,
 * and the top of layouts W / Y / Z ("on a computer? upload right here"). Signed out, picking a
 * file or pasting a link opens the real sign-in over the page and carries on once they're in.
 *
 * `inline` renders it inside another page's own styles (transparent, no page height), with the
 * skin's colours for the bar itself.
 */
import React, { useContext, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { userContext } from "../../../../stores/users/userContext";
import OnboardingModal from "../../../onboarding/modal/OnboardingModal";
import ModalHeader from "../../../onboarding/modal/ModalHeader";
import ModalContent from "../../../onboarding/modal/ModalContent";
import OnboardingFlow from "../../../onboarding/OnboardingFlow";
import SimpleUpload from "./SimpleUpload";
import { SIMPLE_CSS } from "./simpleCss";
import type { HomeSkin } from "../siteLayout/siteSettings";

/** The sign-in sheet any part of a page can ask for; `then` runs once they are signed in. */
export const useSignInSheet = () => {
  const userStore = useContext(userContext);
  const [note, setNote] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const after = useRef<(() => void) | null>(null);
  const requireSignIn = (n: string, then: () => void) => {
    after.current = then;
    userStore.setPhase("get_email");
    setMounted(true);
    setNote(n);
  };
  const sheet = (
    <OnboardingModal open={note !== null} onClose={() => setNote(null)}>
      {mounted ? (
        <>
          <ModalHeader onClose={() => setNote(null)} />
          <ModalContent>
            {note ? <p style={{ fontSize: 14, color: "#fff", textAlign: "center", opacity: 0.75, margin: 0 }}>{note}</p> : null}
            <OnboardingFlow
              onAuthed={() => {
                setNote(null);
                const then = after.current;
                after.current = null;
                if (then) window.setTimeout(then, 0);
              }}
            />
          </ModalContent>
        </>
      ) : null}
    </OnboardingModal>
  );
  return { requireSignIn, sheet };
};

interface Props {
  skin?: HomeSkin;
  /** A short line above the bar, e.g. "On a computer? Upload right here." */
  label?: string;
}

/** The bar on someone else's page (W / Y / Z): brings its own styles and sign-in sheet. */
const UploadBlock: React.FC<Props> = ({ skin = "dark", label }) => {
  const { requireSignIn, sheet } = useSignInSheet();
  return (
    <div className="ccs inline" data-skin={skin}>
      <style>{SIMPLE_CSS}</style>
      {label && <p className="inline-label">{label}</p>}
      <SimpleUpload requireSignIn={requireSignIn} />
      {sheet}
    </div>
  );
};

export default observer(UploadBlock);
